import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Neutral "waiting room" page shown right after the user is redirected back
 * from an OMT / Whish hosted checkout. It exists purely to buy a few seconds
 * while the provider's server-to-server webhook lands. As soon as the
 * payment doc flips to a terminal status we forward to the dedicated
 * /payments/success/:id or /payments/failed/:id page.
 *
 * URL: /payments/:paymentId  (may carry ?status=success|failure hint from OMT/Whish)
 */
export const PaymentStatusPage = () => {
  const { paymentId } = useParams();
  const [searchParams] = useSearchParams();
  const redirectStatus = searchParams.get('status');
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [payment, setPayment] = useState(null);
  const [error, setError] = useState('');
  const isRTL = language === 'ar';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/payments/${paymentId}`, { withCredentials: true });
        if (!cancelled) setPayment(r.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load payment');
      }
    };
    load();
    // Poll every 3s (10× total = ~30s) then slow to 15s. Nudge the
    // provider-specific status fallback once, in case the webhook is delayed.
    let attemptedProviderPoll = false;
    let ticks = 0;
    const iv = setInterval(() => {
      ticks += 1;
      setPayment((p) => {
        if (!p) return p;
        if (p.status === 'pending') {
          load();
          if (!attemptedProviderPoll && (p.provider === 'omt' || p.provider === 'whish')) {
            attemptedProviderPoll = true;
            const url = p.provider === 'omt'
              ? `${API}/payments/${paymentId}/omt/status`
              : `${API}/payments/${paymentId}/whish/status`;
            axios.post(url, {}, { withCredentials: true })
              .then(load)
              .catch(() => {});
          }
        }
        return p;
      });
    }, ticks < 10 ? 3000 : 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [paymentId]);

  // The instant the payment lands on a terminal state, forward to the
  // dedicated confirmation UI.
  useEffect(() => {
    if (!payment) return;
    if (payment.status === 'confirmed') {
      navigate(`/payments/success/${paymentId}`, { replace: true });
    } else if (['failed', 'cancelled', 'rejected'].includes(payment.status)) {
      navigate(`/payments/failed/${paymentId}`, { replace: true });
    }
  }, [payment?.status, paymentId, navigate]);

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <Alert variant="destructive" className="max-w-lg" data-testid="payment-status-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 ${isRTL ? 'rtl' : 'ltr'}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="payment-status-page"
    >
      <Card className="max-w-md w-full p-8 md:p-10 text-center shadow-xl border-2 border-blue-100">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#1e88e5] text-white flex items-center justify-center mb-5">
          <Loader2 className="animate-spin" size={44} />
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-[#2c3e50] mb-3" data-testid="payment-status-title">
          {redirectStatus === 'failure'
            ? t('نتحقق من عملية الدفع...', 'Checking your payment…', 'Vérification du paiement…')
            : t('جاري تأكيد الدفع...', 'Finalizing your payment…', 'Finalisation du paiement…')
          }
        </h1>

        <p className="text-gray-600 mb-2" data-testid="payment-status-subtitle">
          {t('نتواصل مع مزود الدفع للتأكد من العملية. هذا يستغرق عادةً بضع ثوانٍ.',
             'We are confirming with the payment provider. This usually takes a few seconds.',
             'Nous confirmons avec le fournisseur de paiement.')}
        </p>

        {payment?.reference && (
          <p className="mt-4 text-xs font-mono text-gray-500" data-testid="payment-status-reference">
            {t('المرجع', 'Reference', 'Référence')}: <b>{payment.reference}</b>
          </p>
        )}
      </Card>
    </div>
  );
};

export default PaymentStatusPage;
